// Strip job-orchestration noise out of an Ollama job's stdout before we render
// it as user-facing markdown. Without this, ReactMarkdown happily renders:
//   > [Job 123abc] Starting cover_letter
//   [ℹ] Model: qwen3:32b · ctx=16384 · predict=4096
//   Server restarted while job was running. Re-queuing automatically.
// …all of which look like garbage to anyone who isn't debugging the queue.
//
// Used by every place that displays raw `useJobRunner.log`:
//   - DeepResearchDrawer / DeepResearchPanel
//   - ApplicationDetail's ApplyHelper (cover letter / answers / recommendation)
//   - Interview page's PrepPanel + ContactsPanel
//   - Offers' LLM advisor

const NOISE_PATTERNS: RegExp[] = [
  // server.js spawnJob opener: `> [Job 8fa3..] Starting cover_letter`
  /^>?\s*\[Job [a-f0-9-]+\][^\n]*$/gm,

  // server.js startup recovery: re-queue notice
  /^>?\s*Server restarted while job was running[^\n]*$/gm,

  // ollama-client + backend prefixed status lines:
  //   `[ℹ] Model: qwen3:32b · ctx=16384 · predict=4096`
  //   `[ℹ] Generating cover letter for ...`
  //   `[⚠] Couldn't fetch from Greenhouse API. Using URL as fallback context.`
  //   `[✔] PDF: output/cv-foo.pdf`
  /^\s*\[[ℹ⚠✓✔]\][^\n]*$/gm,

  // tailor-cv-backend banner:  `══ CV → JSON ══`
  /^\s*══\s+[^\n]+\s+══\s*$/gm,

  // Defense-in-depth: SSE event JSON ever leaking through (server.js writes
  // `{"type":"exit","code":0}` to job log; sse.ts now filters but in case
  // any path slips by, also strip the literal here so users don't see
  // `{"type":"exit","code":0}` at the end of their LLM output. Anchored
  // strictly so an LLM legitimately discussing SSE in an answer isn't eaten.
  /^\s*\{"type":"(?:exit|stdout|stderr|log|token|done|progress|error)"[^\n]*\}\s*$/gm,
];

export function cleanLLMOutput(raw: string): string {
  if (!raw) return '';
  // 1) Strip ANSI color codes (ESC[...m) — backends emit lots of these.
  // eslint-disable-next-line no-control-regex
  let s = raw.replace(/\x1b\[[0-9;]*m/g, '');
  // 2) Drop noise lines.
  for (const re of NOISE_PATTERNS) s = s.replace(re, '');
  // 3) Collapse 3+ consecutive blank lines (created by step 2) down to 2.
  s = s.replace(/\n{3,}/g, '\n\n');
  return s.trim();
}

// Heavier cleanup for SINGLE-BLOCK PROSE outputs (cover letter, recommendation
// letter). qwen3:32b sometimes ignores "no preamble" prompts and wraps the
// letter in ```markdown ... ``` fences, or sandwiches it with chat-style
// niceties ("Sure! Here's the adapted letter:" / "Let me know if you want
// changes!"). Strip both.
//
// Do NOT use this for multi-section reports (deep research, interview prep)
// — those legitimately have headings that look like preamble at first glance
// (e.g. "Below are the key talking points…").
const PREAMBLE_PATTERN = /^(?:Sure[!.,]?|Certainly[!.,]?|Of course[!.,]?|Absolutely[!.,]?|Here(?:'s| is)\b|Below (?:is|you'll find)\b|I(?:'ve|'ll| have| will)\b)[^\n]*\n+/i;
const POSTAMBLE_PATTERN = /\n+(?:Let me know\b|Hope this (?:helps|is)\b|Feel free to\b|I hope (?:this|the)\b|Please (?:let me|don't|feel)\b|If you (?:need|want|'d)\b|Should you\b)[^\n]*$/i;

export function cleanLetterOutput(raw: string): string {
  let s = cleanLLMOutput(raw);
  // Iterate: peel preamble → postamble → code fences → bold label, in a
  // loop. Each pass might expose another wrapper (e.g. a code fence sitting
  // *inside* a preamble: "Sure! Below is the letter:\n\n```\nDear ...").
  for (let pass = 0; pass < 4; pass++) {
    const before = s;
    // 1) Preamble lines (up to 2 stacked: "Sure!\nHere's the letter:\n")
    for (let i = 0; i < 2; i++) {
      const m = s.match(PREAMBLE_PATTERN);
      if (!m) break;
      s = s.slice(m[0].length).trim();
    }
    // 2) Postamble lines
    for (let i = 0; i < 2; i++) {
      const m = s.match(POSTAMBLE_PATTERN);
      if (!m || m.index == null) break;
      s = s.slice(0, m.index).trim();
    }
    // 3) Wrapping markdown code fence (with or without lang tag)
    s = s.replace(/^```(?:[a-zA-Z]+)?\s*\n/, '').replace(/\n```\s*$/, '').trim();
    // 4) Heading label like `**Cover Letter**` / `**Adapted Recommendation
    //    Letter**` that some models stick above the greeting
    s = s.replace(/^\*\*[^\n]*?(?:Letter|Recommendation)\s*\*\*\s*\n+/i, '').trim();
    if (s === before) break; // no change → done
  }
  return s;
}
